import type { BookkeepingAccessStatus } from './bookkeeping-access-status'
import type { BookkeepingStage } from './bookkeeping-stage'
import type { BookkeepingCycle } from './BookkeepingProgressionInfo'

/**
 *  The bookkeeping display stage.
 */
export type BookkeepingDisplayStage =
  (typeof allBookkeepingDisplayStages)[number]

/**
 * All the bookkeeping display stages.
 */
export const allBookkeepingDisplayStages = [
  'SettingUpJob',
  'PendingAccess',
  'AdminConfirmation',
  'ReadyToStart',
  'AssessingBooks',
  'InProgress',
  'CycleComplete',
  'BookkeepingCompleted',
  'AwaitingAccessConfirmation',
] as const

/**
 * Options to get the display stage options.
 */
export interface BookkeepingDisplayStageOptions {
  stage: BookkeepingStage
  accessStatus: BookkeepingAccessStatus
  cycles: BookkeepingCycle[]
}

/**
 * Get the display stage.
 */
export function getDisplayStage(
  options: BookkeepingDisplayStageOptions,
): BookkeepingDisplayStage {
  if (options.stage === 'Pending') {
    return 'SettingUpJob'
  }

  if (options.stage === 'ProvisioningAccess') {
    switch (options.accessStatus) {
      case 'NotRequested':
      case 'ManagedAccessClientConnectionRequested':
      case 'ManagedAccessClientConfirmationRequested':
        return 'PendingAccess'
      case 'ManagedAccessAdminConfirmationRequested':
        return 'AdminConfirmation'
      case 'UnmanagedAccessProviderConfirmationRequested':
        return 'AwaitingAccessConfirmation'
      default:
        return 'ReadyToStart'
    }
  }

  switch (options.stage) {
    case 'ReadyToStart':
      // When the cycles are defined, treat 'ReadyToStart' as 'CycleComplete'
      // since all cycles have finished.
      if (options.cycles.length > 0) {
        return 'CycleComplete'
      }

      return 'ReadyToStart'
    case 'AssessingBooks':
      return 'AssessingBooks'
    case 'CycleInProgress':
      // When the stage is CycleInProgress, we need to check the last cycle status
      // to determine the step status.
      if (options.cycles.at(-1)?.status?.type === 'Completed') {
        return 'CycleComplete'
      }

      return 'InProgress'
    case 'Completed':
      return 'BookkeepingCompleted'

    default:
      return 'ReadyToStart'
  }
}
